import { useState } from "react";
import clsx from "clsx";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

import { Button } from "../../../components/base/Button";
import useTicketMutations from "../../../data/Ticket/useTicketMutations";
import { TicketPriority, TicketType } from "../../../models/Tickets/Ticket";
import {
  ITicketInsightEscalationRisk,
  ITicketInsightPriorityMismatch,
  ITicketInsightTypeMismatch,
} from "../../../models/Tickets/TicketInsight";
import {
  pillBaseClasses,
  priorityClassMap,
  ticketPriorityLabels,
  TicketTypePill,
  typeClassMap,
  ticketTypeLabels,
} from "../ticketMeta";
import InsightTicketRow from "./InsightTicketRow";
import { Card, EmptyState } from "./InsightWidgets";

const PAGE = 8;

function useShowMore<T>(items: T[]) {
  const [limit, setLimit] = useState(PAGE);
  const visible = items.slice(0, limit);
  const more = items.length - visible.length;
  return {
    visible,
    more,
    showMore: () => setLimit((l) => l + PAGE),
  };
}

/**
 * Applied fixes vanish from the list straight away; the API's analytics cache
 * catches up on the next refetch, so there is no stale "still wrong" row.
 */
function useApplied() {
  const [applied, setApplied] = useState<Set<string>>(new Set());
  return {
    isApplied: (id: string) => applied.has(id),
    markApplied: (id: string) =>
      setApplied((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      }),
  };
}

export function TypeMismatchCard({
  mismatches,
}: {
  mismatches: ITicketInsightTypeMismatch[];
}) {
  const { update } = useTicketMutations();
  const { isApplied, markApplied } = useApplied();
  const pending = mismatches.filter((m) => !isApplied(m.ticket.ticketId));
  const { visible, more, showMore } = useShowMore(pending);

  const applyType = (ticketId: string, type: TicketType) =>
    update.mutate(
      { id: ticketId, type },
      { onSuccess: () => markApplied(ticketId) },
    );

  return (
    <Card
      title="Type disagreements"
      description="Tickets filed as Bug that read as a feature request, or vice versa. Confident calls only."
      action={
        pending.length > 0 ? (
          <span className={clsx(pillBaseClasses, "bg-rose-100 text-rose-700")}>
            {pending.length}
          </span>
        ) : undefined
      }
    >
      {pending.length === 0 ? (
        <EmptyState>The manual types agree with the model. Nice.</EmptyState>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map(({ ticket, suggestedType }) => (
            <InsightTicketRow
              key={ticket.ticketId}
              ticket={ticket}
              extra={
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <TicketTypePill type={ticket.type} />
                  <ArrowRightIcon className="h-3 w-3" />
                  <span
                    className={clsx(pillBaseClasses, typeClassMap[suggestedType])}
                  >
                    {ticketTypeLabels[suggestedType]}
                  </span>
                  <span className="text-gray-400">
                    ({Math.round(ticket.categoryConfidence * 100)}% sure)
                  </span>
                </span>
              }
              actions={
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={update.isPending}
                  onClick={() => applyType(ticket.ticketId, suggestedType)}
                >
                  Set to {ticketTypeLabels[suggestedType]}
                </Button>
              }
            />
          ))}
          {more > 0 ? (
            <button
              type="button"
              onClick={showMore}
              className="w-full py-2 text-center text-xs text-orange-700 hover:underline"
            >
              Show {Math.min(PAGE, more)} more
            </button>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export function PriorityMismatchCard({
  mismatches,
}: {
  mismatches: ITicketInsightPriorityMismatch[];
}) {
  const { update } = useTicketMutations();
  const { isApplied, markApplied } = useApplied();
  const pending = mismatches.filter((m) => !isApplied(m.ticket.ticketId));
  const { visible, more, showMore } = useShowMore(pending);

  const applyPriority = (ticketId: string, priority: TicketPriority) =>
    update.mutate(
      { id: ticketId, priority },
      { onSuccess: () => markApplied(ticketId) },
    );

  return (
    <Card
      title="Possibly under-prioritised"
      description="Active tickets where the text reads more urgent than the priority they were given."
      action={
        pending.length > 0 ? (
          <span className={clsx(pillBaseClasses, "bg-orange-100 text-orange-800")}>
            {pending.length}
          </span>
        ) : undefined
      }
    >
      {pending.length === 0 ? (
        <EmptyState>Nothing active looks under-prioritised.</EmptyState>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map(({ ticket, gap }) => (
            <InsightTicketRow
              key={ticket.ticketId}
              ticket={ticket}
              extra={
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <ArrowRightIcon className="h-3 w-3" />
                  <span
                    className={clsx(
                      pillBaseClasses,
                      priorityClassMap[ticket.suggestedPriority],
                    )}
                  >
                    {ticketPriorityLabels[ticket.suggestedPriority]}
                  </span>
                  {gap > 1 ? (
                    <span className="font-medium text-rose-600">
                      +{gap} levels
                    </span>
                  ) : null}
                </span>
              }
              actions={
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={update.isPending}
                  onClick={() =>
                    applyPriority(ticket.ticketId, ticket.suggestedPriority)
                  }
                >
                  Set {ticketPriorityLabels[ticket.suggestedPriority]}
                </Button>
              }
            />
          ))}
          {more > 0 ? (
            <button
              type="button"
              onClick={showMore}
              className="w-full py-2 text-center text-xs text-orange-700 hover:underline"
            >
              Show {Math.min(PAGE, more)} more
            </button>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function riskClass(score: number) {
  if (score >= 8) return "bg-red-600 text-white";
  if (score >= 6) return "bg-orange-500 text-white";
  return "bg-amber-300 text-amber-900";
}

export function EscalationRiskCard({
  risks,
}: {
  risks: ITicketInsightEscalationRisk[];
}) {
  const { visible, more, showMore } = useShowMore(risks);

  return (
    <Card
      title="Escalation risk"
      description="Active tickets most likely to turn into an angry phone call: urgent, unhappy, unanswered, or just old."
      action={
        risks.length > 0 ? (
          <span className={clsx(pillBaseClasses, "bg-red-100 text-red-700")}>
            {risks.length}
          </span>
        ) : undefined
      }
    >
      {risks.length === 0 ? (
        <EmptyState>Nothing active is scoring high for escalation risk.</EmptyState>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map(({ ticket, score, reasons }) => (
            <div key={ticket.ticketId} className="flex items-start gap-3 py-2">
              <span
                className={clsx(
                  "mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold",
                  riskClass(score),
                )}
                title="Risk score out of 10"
              >
                {score}
              </span>
              <div className="min-w-0 flex-1">
                <InsightTicketRow
                  ticket={ticket}
                  showSummary={false}
                  className="py-0"
                />
                <div className="mt-1 flex flex-wrap gap-1">
                  {reasons.map((reason) => (
                    <span
                      key={reason}
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                    >
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {more > 0 ? (
            <button
              type="button"
              onClick={showMore}
              className="w-full py-2 text-center text-xs text-orange-700 hover:underline"
            >
              Show {Math.min(PAGE, more)} more
            </button>
          ) : null}
        </div>
      )}
    </Card>
  );
}
